/**
 * useFCMNotifications
 * – Registers for Firebase Cloud Messaging (FCM) via @capacitor/push-notifications
 * – Saves the FCM token to the backend so it can send native Android push notifications
 * – Listens for foreground notifications and calls onOpenTask on tap
 *
 * Only active when running inside a Capacitor Android/iOS app.
 * Falls back silently in web browser.
 */
import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { apiFetch } from '../lib/apiFetch';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface UseFCMOptions {
  userId: string | undefined;
  onOpenTask?: (taskId: string) => void;
}

export function useFCMNotifications({ userId, onOpenTask }: UseFCMOptions) {
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!userId || registeredRef.current) return;
    if (!Capacitor.isNativePlatform()) return; // samo na Android/iOS

    let PushNotifications: any = null;

    async function setup() {
      try {
        // Dinamički import - radi samo unutar Capacitor konteksta
        const mod = await import('@capacitor/push-notifications');
        PushNotifications = mod.PushNotifications;

        // 1. Provjeri i zatraži dozvolu
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') {
          console.warn('[FCM] Push notification permission not granted');
          return;
        }

        // 2. Registruj se za FCM
        await PushNotifications.register();

        // 3. Kada dobijemo FCM token, sačuvaj ga na backend
        await PushNotifications.addListener('registration', async (token: { value: string }) => {
          console.log('[FCM] Token received:', token.value.substring(0, 20) + '...');
          try {
            await apiFetch(`${API_BASE}/fcm/token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId, token: token.value }),
            });
            registeredRef.current = true;
            console.log('[FCM] Token saved to backend');
          } catch (err) {
            console.warn('[FCM] Failed to save token:', err);
          }
        });

        // 4. Greška pri registraciji
        await PushNotifications.addListener('registrationError', (err: any) => {
          console.warn('[FCM] Registration error:', err);
        });

        // 5. Notifikacija primljena dok je app u foregroundu - prikaži je
        await PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
          console.log('[FCM] Foreground notification:', notification.title);
          // App je otvorena - notifikacija se automatski prikazuje kao native na Androidu 13+
          // Ovdje možemo dodati in-app logiku ako treba
        });

        // 6. Korisnik kliknuo na notifikaciju
        await PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
          const taskId = action.notification?.data?.taskId;
          if (taskId && onOpenTask) {
            onOpenTask(taskId);
          }
        });

      } catch (err) {
        console.warn('[FCM] Setup failed:', err);
      }
    }

    setup();

    return () => {
      // Cleanup listenera kada se komponenta unmountuje
      if (PushNotifications) {
        PushNotifications.removeAllListeners().catch(() => {});
      }
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
}
